import React, { useContext, useState, useEffect } from "react";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";

import GardenContext from "../context/GardenContext";
import apiClient from "../utils/apiClient";

const GardenForm = ({ open, onClose, garden }) => {
  const {
    selectedGarden: contextSelectedGarden,
    setSidebarOpen,
    addGarden,
    updateGarden,
  } = useContext(GardenContext);
  
  // Use prop garden if provided (for modal), otherwise use context
  const selectedGarden = garden || contextSelectedGarden;

  const [formState, setFormState] = useState({
    name: "",
    width: "",
    depth: "",
    unit: "meters",
  });

  useEffect(() => {
    if (selectedGarden) {
      setFormState(selectedGarden);
    } else {
      setFormState({ name: "", width: "", depth: "", unit: "meters" });
    }
  }, [selectedGarden]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedGarden) {
        // Update existing garden
        const response = await apiClient.put(
          `/gardens/${selectedGarden.garden_uuid}`,
          formState
        );
        updateGarden(response.data);
        console.log("Garden updated:", response.data);
      } else {
        // Create new garden
        const response = await apiClient.post("/gardens", formState);
        addGarden(response.data);
        console.log("Garden created:", response.data);
      }
      if (setSidebarOpen) {
        setSidebarOpen(false);
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error("Failed to submit form:", err);
      setError("Failed to save garden. Please try again.");
    }
  };

  const handleDialogClose = () => {
    setFormState({ name: "", width: "", depth: "", unit: "meters" });
    setError("");
    if (onClose) {
      onClose();
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <TextField
        fullWidth
        label="Name"
        name="name"
        value={formState.name}
        onChange={handleChange}
        margin="normal"
        className="garden-form-field"
      />
      <TextField
        fullWidth
        label="Width"
        name="width"
        value={formState.width}
        onChange={handleChange}
        margin="normal"
        type="number"
        className="garden-form-field"
      />
      <TextField
        fullWidth
        label="Depth"
        name="depth"
        value={formState.depth}
        onChange={handleChange}
        margin="normal"
        type="number"
        className="garden-form-field"
      />
      <FormControl fullWidth margin="normal" className="garden-form-field">
        <InputLabel id="unit-label">Unit</InputLabel>
        <Select
          labelId="unit-label"
          name="unit"
          value={formState.unit}
          onChange={handleChange}
        >
          <MenuItem value="meters">Meters</MenuItem>
          <MenuItem value="feet">Feet</MenuItem>
        </Select>
      </FormControl>
    </form>
  );

  // If open prop is provided, render as Dialog (modal)
  if (open !== undefined) {
    return (
      <Dialog 
        open={open} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        className="garden-form-modal"
      >
        <DialogTitle className="garden-form-modal-title">
          {selectedGarden ? "Edit Garden" : "Add New Garden"}
        </DialogTitle>
        <DialogContent>
          {formContent}
        </DialogContent>
        <DialogActions className="garden-form-modal-actions">
          <Button onClick={handleDialogClose} className="garden-form-cancel-button">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            className="garden-form-submit-button"
          >
            {selectedGarden ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Otherwise, render as before (for backward compatibility with drawer)
  return (
    <Box sx={{ padding: 2, width: 300 }}>
      {formContent}
      <Button 
        type="submit" 
        variant="contained" 
        color="primary" 
        fullWidth
        onClick={handleSubmit}
        sx={{ mt: 2 }}
      >
        {selectedGarden ? "Update" : "Add"}
      </Button>
    </Box>
  );
};

export default GardenForm;
